import { useState } from 'react';
import { usd, ApiClientError } from '../lib/api';
import { useResource } from '../hooks/useResource';
import { formatDateTime } from '../lib/format';
import { Alert, Badge, Card, Empty, Spinner, Stat } from '../components/ui';

function tone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 'completed') return 'success';
  if (status === 'rejected') return 'danger';
  return 'warning';
}

/**
 * USD balance from Stripe card onramps + payout requests. The balance settles
 * to the integrator's Rubies (NGN) account — requests are fulfilled manually by
 * the platform, which credits the account and marks the request completed.
 */
export function UsdPayouts() {
  const balance = useResource(() => usd.balance(), [], { refreshMs: 20000 });
  const history = useResource(() => usd.payouts(1), [], { refreshMs: 20000 });

  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const available = Number(balance.data?.available_usd?.replace(/,/g, '') ?? '0');

  async function submit() {
    setError(null);
    setOk(false);
    setBusy(true);
    try {
      await usd.requestPayout(amount, note || undefined);
      setAmount('');
      setNote('');
      setOk(true);
      await Promise.all([balance.reload(), history.reload()]);
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not request payout');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-header">
        <h1>USD balance & payouts</h1>
        <p>
          USD earned from Stripe card onramps. Request a payout and we settle it to your Rubies
          (NGN) account, then mark it completed.
        </p>
      </div>

      {error && <Alert>{error}</Alert>}
      {ok && <Alert tone="success">Payout requested — we'll credit your Rubies account and mark it completed.</Alert>}

      <div className="stat-grid" style={{ marginBottom: 16 }}>
        <Stat label="Available" value={balance.data ? `$${balance.data.available_usd}` : '—'} />
        <Stat label="Earned (all-time)" value={balance.data ? `$${balance.data.earned_usd}` : '—'} />
        <Stat label="Requested / paid" value={balance.data ? `$${balance.data.reserved_usd}` : '—'} />
      </div>

      <div style={{ display: 'grid', gap: 16, maxWidth: 560 }}>
        <Card title="Request a payout">
          <div className="card-body">
            <p className="muted" style={{ marginTop: 0 }}>
              Settles to your Rubies NGN account. We convert and credit you, then mark it completed.
            </p>
            <label className="field">
              <span>Amount (USD)</span>
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="100.00"
                inputMode="decimal"
              />
            </label>
            <label className="field">
              <span>Note (optional)</span>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="reference" />
            </label>
            <button
              className="btn btn-primary"
              disabled={busy || !amount || available <= 0}
              onClick={submit}
            >
              {busy ? 'Requesting…' : 'Request payout'}
            </button>
            {available <= 0 && (
              <p className="muted" style={{ fontSize: 12.5 }}>
                No USD available to pay out yet.
              </p>
            )}
          </div>
        </Card>

        <Card title="Payout requests">
          {!history.data ? (
            <Spinner />
          ) : !history.data.items.length ? (
            <Empty>No payout requests yet.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Note</th>
                    <th>Requested</th>
                    <th>Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {history.data.items.map((p) => (
                    <tr key={p.id}>
                      <td className="mono">${p.amount_usd}</td>
                      <td>
                        <Badge tone={tone(p.status)}>{p.status}</Badge>
                      </td>
                      <td className="muted">{p.admin_note || p.note || '—'}</td>
                      <td className="muted">{formatDateTime(p.created_at)}</td>
                      <td className="muted">{p.completed_at ? formatDateTime(p.completed_at) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
